import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import MapGL, { Marker } from "react-map-gl";
import type { MapRef } from "react-map-gl";

type Coordinates = [number, number];

export type RichLauncherMapHandle = {
  flyTo: (opts: Parameters<MapRef["flyTo"]>[0]) => void;
  fitBounds: (
    bounds: Parameters<MapRef["fitBounds"]>[0],
    opts?: Parameters<MapRef["fitBounds"]>[1],
  ) => void;
  getMap: () => ReturnType<MapRef["getMap"]> | null;
  isReady: () => boolean;
};

type RichLauncherMapProps = {
  center: { lng: number; lat: number };
  hidden: boolean;
  accentColor: string;
  mapTheme: string;
  map3dObjects: boolean;
  map3dBuildings: boolean;
  map3dTrees: boolean;
  showPoi: boolean;
  activeMarker: { coordinates: Coordinates; number: number } | null;
  onReady: () => void;
};

function applyBasemapTheme(
  map: { setConfigProperty?: (a: string, b: string, c: unknown) => void },
  theme: string,
  show3dObjects: boolean,
  show3dBuildings: boolean,
  show3dTrees: boolean,
  showPoi: boolean,
) {
  try {
    map.setConfigProperty?.("basemap", "theme", theme);
    map.setConfigProperty?.("basemap", "show3dObjects", show3dObjects);
    map.setConfigProperty?.("basemap", "show3dBuildings", show3dBuildings);
    map.setConfigProperty?.("basemap", "show3dTrees", show3dTrees);
    map.setConfigProperty?.("basemap", "showPointOfInterestLabels", showPoi);
  } catch {
    /* Style may not be Mapbox Standard */
  }
}

const RichLauncherMap = forwardRef<RichLauncherMapHandle, RichLauncherMapProps>(
  (
    {
      center,
      hidden,
      accentColor: _accentColor,
      mapTheme,
      map3dObjects,
      map3dBuildings,
      map3dTrees,
      showPoi,
      activeMarker,
      onReady,
    },
    ref,
  ) => {
    const mapRef = useRef<MapRef | null>(null);
    const [ready, setReady] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        flyTo: (opts) => mapRef.current?.flyTo(opts),
        fitBounds: (bounds, opts) => mapRef.current?.fitBounds(bounds, opts),
        getMap: () => mapRef.current?.getMap?.() ?? null,
        isReady: () => ready,
      }),
      [ready],
    );

    useEffect(() => {
      const map = mapRef.current?.getMap?.();
      if (!map?.isStyleLoaded?.()) return;
      applyBasemapTheme(map, mapTheme, map3dObjects, map3dBuildings, map3dTrees, showPoi);
    }, [mapTheme, map3dObjects, map3dBuildings, map3dTrees, showPoi, ready]);

    return (
      <div
        className={`tripper-rich-launcher__map${hidden ? " tripper-rich-launcher__map--hidden" : ""}`}
      >
        <MapGL
          ref={(instance) => {
            mapRef.current = instance;
          }}
          mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
          mapStyle="mapbox://styles/tnb3k/cmmwgvqp5000701s6eq903b94"
          initialViewState={{
            longitude: center.lng,
            latitude: center.lat,
            zoom: 10,
            pitch: 0,
            bearing: 0,
          }}
          maxPitch={85}
          style={{ width: "100%", height: "100%" }}
          attributionControl={false}
          interactive={false}
          keyboard={false}
          dragPan={false}
          scrollZoom={false}
          doubleClickZoom={false}
          touchZoomRotate={false}
          touchPitch={false}
          dragRotate={false}
          onLoad={(e) => {
            try {
              const canvas = e.target.getCanvas?.();
              if (canvas) {
                canvas.setAttribute("tabindex", "-1");
                canvas.style.outline = "none";
              }
            } catch {
              /* guard against focus-related errors in iframe contexts */
            }
            applyBasemapTheme(
              e.target,
              mapTheme,
              map3dObjects,
              map3dBuildings,
              map3dTrees,
              showPoi,
            );
            setReady(true);
            onReady();
          }}
        >
          {activeMarker && (
            <Marker
              longitude={activeMarker.coordinates[0]}
              latitude={activeMarker.coordinates[1]}
              anchor="center"
            >
              <div
                className="tripper-rich-launcher__marker"
              >
                {activeMarker.number}
              </div>
            </Marker>
          )}
        </MapGL>

        <div className="tripper-rich-launcher__map-attr">
          &copy;{" "}
          <a
            href="https://www.mapbox.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Mapbox
          </a>{" "}
          &copy;{" "}
          <a
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noopener noreferrer"
          >
            OSM
          </a>
        </div>
      </div>
    );
  },
);

RichLauncherMap.displayName = "RichLauncherMap";

export default RichLauncherMap;
